import { useState, useEffect } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { apiService } from '@/services/apiService';
import { CheckCircle2, XCircle, Brain, Activity, Clock, Tag } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [memories, setMemories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [agentsData, tasksData, memoriesData, activitiesData] = await Promise.all([
        apiService.getAgents(),
        apiService.getTasks(),
        apiService.getPublicMemories(100),
        apiService.getPublicActivities(100),
      ]);
      setAgents(agentsData);
      setTasks(tasksData);
      setMemories(memoriesData);
      setActivities(activitiesData);
    } finally {
      setLoading(false);
    }
  };

  const getAgentTaskCount = (agentId) => {
    return tasks.filter(t => t.owner_agent === agentId).length;
  };

  const getAgentMemories = (agentId) => {
    return memories.filter(m => m.agente_origem === agentId);
  };

  const getAgentActivities = (agentId) => {
    return activities.filter(a => a.agente_id === agentId);
  };

  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Carregando...</div>;
  }

  return (
    <div data-testid="agents-page">
      <PageHeader
        title="Agentes"
        description="Gerenciamento e monitoramento dos agentes do ecossistema"
      />

      {/* Agent Cards Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 mb-8">
        {agents.map((agent) => {
          const taskCount = getAgentTaskCount(agent.id);
          const memoryCount = getAgentMemories(agent.id).length;
          const activityCount = getAgentActivities(agent.id).length;
          
          return (
            <div
              key={agent.id}
              className="rounded-lg border border-border/50 bg-card/40 backdrop-blur-sm hover:border-primary/30 transition-all cursor-pointer"
              onClick={() => handleAgentClick(agent)}
              data-testid="agent-card"
            >
              <div className="p-5">
                <div className="flex items-start gap-4 mb-4">
                  {/* Avatar aumentado: h-16 w-16 (antes h-14 w-14) */}
                  <div
                    className="h-16 w-16 rounded-lg bg-cover bg-center border-2 flex-shrink-0"
                    style={{
                      backgroundImage: `url(${agent.avatar})`,
                      borderColor: agent.color,
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-xl font-bold" style={{ color: agent.color }}>
                      {agent.name}
                    </h3>
                    <p className="text-xs text-muted-foreground uppercase mt-1">{agent.specialty}</p>
                  </div>
                </div>

                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <div className="flex items-center gap-2">
                      {agent.enabled ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          <span className="text-emerald-400 uppercase text-xs font-bold">Ativo</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="text-red-400 uppercase text-xs font-bold">Inativo</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Tarefas</span>
                    <span className="font-mono text-xs font-bold">{taskCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Brain className="h-3 w-3" /> Memórias
                    </span>
                    <span className="font-mono text-xs">{memoryCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Activity className="h-3 w-3" /> Atividades
                    </span>
                    <span className="font-mono text-xs">{activityCount}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {agent.permissions.slice(0, 3).map((perm) => (
                    <span
                      key={perm}
                      className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest border bg-primary/10 text-primary border-primary/20"
                    >
                      {perm}
                    </span>
                  ))}
                  {agent.permissions.length > 3 && (
                    <span className="text-[9px] text-muted-foreground">
                      +{agent.permissions.length - 3}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Memories & Activities Side by Side - Responsive */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Public Memories Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Brain className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-lg font-bold uppercase tracking-wider">Memórias Públicas</h2>
            <span className="text-xs text-muted-foreground">({memories.length})</span>
          </div>
          <div className="space-y-3">
            {memories.slice(0, 5).map((memory) => {
              const agent = agents.find(a => a.id === memory.agente_origem);
              return (
                <div
                  key={memory.id}
                  className="rounded-lg border border-border/50 bg-card/40 backdrop-blur-sm p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <span 
                      className="text-xs font-bold uppercase"
                      style={{ color: agent?.color || '#888' }}
                    >
                      {agent?.name || memory.agente_origem}
                    </span>
                    <span className="text-[10px] text-muted-foreground uppercase bg-muted/30 px-1.5 py-0.5 rounded">
                      {memory.tipo}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold mb-1 line-clamp-1">{memory.titulo}</h4>
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{memory.conteudo}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatDate(memory.criado_em)}
                    </div>
                    {memory.tags && memory.tags.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Tag className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground">
                          {memory.tags.slice(0, 2).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Public Activities Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Activity className="h-5 w-5 text-emerald-500" />
            <h2 className="font-heading text-lg font-bold uppercase tracking-wider">Atividades Recentes</h2>
            <span className="text-xs text-muted-foreground">({activities.length})</span>
          </div>
          <div className="rounded-lg border border-border/50 bg-card/40 backdrop-blur-sm">
            <div className="divide-y divide-border/30">
              {activities.slice(0, 8).map((activity) => {
                // Normalizar agent_id: "tom" → "main" (Tom usa "tom" em atividades mas "main" no sistema)
                const normalizedAgentId = activity.agente_id === 'tom' ? 'main' : activity.agente_id;
                const agent = agents.find(a => a.id === normalizedAgentId);
                // Fallback: DiceBear avatar para subagentes
                const avatarUrl = agent?.avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${activity.agente_id || 'subagent'}`;
                const agentColor = agent?.color || activity.agente_cor || '#666';
                return (
                  <div key={activity.id} className="flex items-center gap-3 p-3">
                    {/* Avatar com fallback DiceBear */}
                    <div
                      className="h-10 w-10 rounded-full bg-cover bg-center border flex-shrink-0"
                      style={{
                        backgroundImage: `url(${avatarUrl})`,
                        borderColor: agentColor,
                        backgroundColor: '#1a1a1e',
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        <span className="font-semibold" style={{ color: agentColor }}>
                          {activity.agente_nome || activity.agente_id || 'Subagente'}
                        </span>
                        {' '}<span className="line-clamp-1">{activity.acao}</span>
                      </p>
                      {activity.projeto_nome && (
                        <p className="text-[10px] text-primary">{activity.projeto_nome}</p>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                      {formatDate(activity.criado_em)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Agent Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121215] border-border max-w-3xl max-h-[85vh]">
          {selectedAgent && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  {/* Avatar aumentado no dialog: h-20 w-20 (antes h-16 w-16) */}
                  <div
                    className="h-20 w-20 rounded-lg bg-cover bg-center border-2"
                    style={{
                      backgroundImage: `url(${selectedAgent.avatar})`,
                      borderColor: selectedAgent.color,
                    }}
                  />
                  <div>
                    <DialogTitle className="font-heading text-2xl" style={{ color: selectedAgent.color }}>
                      {selectedAgent.name}
                    </DialogTitle>
                    <p className="text-xs text-muted-foreground uppercase mt-1">{selectedAgent.specialty}</p>
                  </div>
                </div>
              </DialogHeader>

              <Tabs defaultValue="info" className="mt-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="info">Info</TabsTrigger>
                  <TabsTrigger value="tasks">Tarefas</TabsTrigger>
                  <TabsTrigger value="memories">Memórias</TabsTrigger>
                  <TabsTrigger value="activities">Atividades</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="mt-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-xs uppercase text-muted-foreground">ID</span>
                        <p className="mt-1 font-mono text-xs">{selectedAgent.id}</p>
                      </div>
                      <div>
                        <span className="text-xs uppercase text-muted-foreground">Status</span>
                        <div className="mt-1 flex items-center gap-2">
                          {selectedAgent.enabled ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                              <span className="text-emerald-400 uppercase text-xs font-bold">Ativo</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 text-red-500" />
                              <span className="text-red-400 uppercase text-xs font-bold">Inativo</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs uppercase text-muted-foreground">Permissões</span>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selectedAgent.permissions.map((perm) => (
                          <span
                            key={perm}
                            className="inline-flex items-center rounded-sm px-2 py-1 text-[10px] font-bold uppercase tracking-widest border bg-primary/10 text-primary border-primary/20"
                          >
                            {perm}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="tasks" className="mt-4">
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {tasks.filter(t => t.owner_agent === selectedAgent.id).length > 0 ? (
                        tasks.filter(t => t.owner_agent === selectedAgent.id).map(task => (
                          <div key={task.id} className="rounded-md border border-border/50 p-3 bg-background/40">
                            <p className="text-sm font-medium">{task.title}</p>
                            <p className="text-xs text-muted-foreground mt-1 uppercase">{task.status}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">Nenhuma tarefa atribuída</p>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="memories" className="mt-4">
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {getAgentMemories(selectedAgent.id).length > 0 ? (
                        getAgentMemories(selectedAgent.id).map(memory => (
                          <div key={memory.id} className="rounded-md border border-border/50 p-3 bg-background/40">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs uppercase text-muted-foreground">{memory.tipo}</span>
                              <span className="text-[10px] text-muted-foreground">{formatDate(memory.criado_em)}</span>
                            </div>
                            <p className="text-sm font-medium">{memory.titulo}</p>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{memory.conteudo}</p>
                            {memory.tags && memory.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {memory.tags.map(tag => (
                                  <span key={tag} className="text-[9px] bg-muted/30 px-1.5 py-0.5 rounded">{tag}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">Nenhuma memória registrada</p>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="activities" className="mt-4">
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {getAgentActivities(selectedAgent.id).length > 0 ? (
                        getAgentActivities(selectedAgent.id).map(activity => (
                          <div key={activity.id} className="rounded-md border border-border/50 p-3 bg-background/40">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium">{activity.acao}</span>
                              <span className="text-[10px] text-muted-foreground">{formatDate(activity.criado_em)}</span>
                            </div>
                            {activity.contexto && (
                              <p className="text-xs text-muted-foreground">{activity.contexto}</p>
                            )}
                            {activity.projeto_nome && (
                              <p className="text-[10px] text-primary mt-1">Projeto: {activity.projeto_nome}</p>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">Nenhuma atividade registrada</p>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
